import { useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister
} from 'react-hook-form';
import { CustomerBo } from '@utils/types/back-office/customer-bo.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import PhoneField from '@components/atoms/ui-kit/phone-input.tsx';
import { BATCH_PRIORITY_OPTIONS } from '../update-customer-form-schema';

interface Props {
  register: UseFormRegister<CustomerBo>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<CustomerBo, any>;
  errors: FieldErrors<CustomerBo>;
  customer?: CustomerBo;
  cardStyle: React.CSSProperties;
}

export function CustomerOrgDetailsTab({
  register,
  control,
  errors,
  customer,
  cardStyle
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 py-4">
      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6"
        style={cardStyle}
      >
        <PageHeader
          title={t('basic-information')}
          caption={t('update-account-description')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 lg:gap-6">
          <Input
            id="name"
            label={t('customer-name')}
            defaultValue={customer?.name || ''}
            type="text"
            autoComplete="name"
            {...register('name')}
            error={errors?.name?.message}
          />
          <Input
            id="email"
            label={t('email-address-for-billing')}
            defaultValue={customer?.email || ''}
            type="email"
            {...register('email')}
            autoComplete="email"
            error={errors?.email?.message}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field: { value, onChange } }) => (
              <PhoneField
                id="phone"
                label={t('phone')}
                value={value ?? ''}
                onChange={onChange}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                defaultCountry={(customer?.address_country_code || 'NL') as any}
                placeholder={t('enter-full-phone-number')}
                autoComplete="tel"
                wrapperClass="w-full max-w-full"
                className="w-0 flex-1 min-w-0"
                messages={{
                  required: t('this-field-is-required'),
                  invalid: t('please-enter-a-valid-phone-number')
                }}
                error={errors.phone && errors.phone.message}
              />
            )}
          />
          <Input
            id="max_users"
            label={t('max-users')}
            defaultValue={customer?.max_users || 3}
            type="number"
            {...register('max_users')}
            autoComplete="max_users"
            error={errors?.max_users?.message}
          />
        </div>
      </div>

      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6"
        style={cardStyle}
      >
        <PageHeader
          title={t('batch-processing-priority')}
          caption={
            t(
              'control-how-quickly-transcription-jobs-are-processed-gpu-autoscaling'
            ) || ''
          }
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        />
        <Controller
          control={control}
          name="batch_priority"
          render={({ field: { onChange, value } }) => (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {BATCH_PRIORITY_OPTIONS.map(option => {
                  const isSelected = value === option.value;
                  return (
                    <div
                      key={option.value}
                      className="flex items-start gap-x-3"
                    >
                      <input
                        id={`batch_priority_update_${option.value}`}
                        type="radio"
                        value={option.value}
                        checked={isSelected}
                        onChange={() => onChange(option.value)}
                        className="h-4 w-4 mt-0.5 border-hairline text-primary focus:ring-primary"
                      />
                      <label
                        htmlFor={`batch_priority_update_${option.value}`}
                        className="block text-sm leading-6 cursor-pointer"
                      >
                        <span className="font-medium">
                          {option.label}
                          <span className="ml-1.5 text-xs opacity-60">
                            ({option.value})
                          </span>
                        </span>
                        <span className="block text-xs opacity-70 mt-0.5">
                          {option.description}
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 rounded-2xl border bg-blue-500/10 border-blue-500/25 dark:bg-blue-500/15 dark:border-blue-500/35">
                <div className="text-xs leading-relaxed text-blue-900 dark:text-blue-300">
                  <p className="font-semibold mb-1">
                    {t('batch-priority-help-title') || 'About Batch Priority'}
                  </p>
                  <p className="opacity-90">
                    {t('batch-priority-help-description') ||
                      'Lower values = Higher priority = Faster processing. Wait times are estimates during peak hours (0-9am UTC).'}
                  </p>
                </div>
              </div>

              {errors.batch_priority && (
                <span className="text-red-500 text-xs font-light px-2 block mt-2">
                  {errors.batch_priority.message}
                </span>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}
